import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import withRouter from "../withRouter.js";
import {
  ccountry,
  clientname,
  currency,
  currentTenant,
  logo,
  thousandsdisplay,
  userid,
} from "../Common/Credentials.js";
import { spin } from "../Common/NewLoader.jsx";
import { inventoryid } from "../Common/Permissions.js";
import InventoryService from "./InventoryService.js";
import { NumericFormat } from "react-number-format";
import { IconButton, Tooltip, TextField, MenuItem } from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import Swal from "sweetalert2";
import { appmasterid } from "../Common/Permissions.js";

import EditIcon from "../images/Icons/edit.png"
import DeleteIcon from "../images/Icons/delete.png"
import AddIcon from "../images/Icons/plussolid.png"
import '../NewUI/NewUI.css'
import Breadcrumb from "../NewUI/NewComponents/Breadcrumb.jsx";

const ItemsDataTable = ({ navigate, location }) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [tenantdetails, setTenantdetails] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [isActionHovered, setIsActionHovered] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filteredSidebarData, setFilteredSidebarData] = useState([]);
  const [hasScrolledToSelected, setHasScrolledToSelected] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const sidebarRef = React.useRef(null);
  const scrollTimeout = React.useRef(null);

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'brand':
          aValue = a.brand || '';
          bValue = b.brand || '';
          break;
        case 'stock':
          aValue = a.stock || 0;
          bValue = b.stock || 0;
          break;
        case 'cost':
          aValue = a.cost || 0;
          bValue = b.cost || 0;
          break;
        case 'itemcode':
          aValue = a.itemcode || '';
          bValue = b.itemcode || '';
          break;
        case 'hsn':
          aValue = a.hsn || '';
          bValue = b.hsn || '';
          break;
        case 'tax':
          aValue = a.tax || 0;
          bValue = b.tax || 0;
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'description':
          aValue = a.description || '';
          bValue = b.description || '';
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: "UI-search-highlight"
        }, part)
        : part
    );
  };

  // Format currency as string for highlighting
  const formatCurrencyAsString = (value) => {
    if (value === null || value === undefined) return '0';

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  // Updated refreshItems with proper loading management and error handling
  const refreshItems = useCallback(() => {
    setLoading(true);

    InventoryService.GetTenantdetails(currentTenant)
      .then((res) => setTenantdetails(res.data.country))
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          alert("Your Session is Lost: Please login Back to resume");
          navigate("/");
        }
      });

    InventoryService.retriveAllItems(currentTenant)
      .then((response) => {
        if (response.data) {
          const sortedItems = response.data.sort((a, b) => (a.id < b.id ? 1 : -1));
          setItems(sortedItems);
          setFilteredItems(sortedItems);
        }
      })
      .catch((error) => {
        console.error("Error retrieving items:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  // Updated useEffect with session check, loading, and safe location.state access
  useEffect(() => {
    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    setLoading(true);
    refreshItems();

    if (location.state != null) {
      setMessage(location.state.message || "");
    } else {
      setMessage("");
    }

    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [location.state, navigate, refreshItems]);

  useEffect(() => {
    // Auto-scroll in detail view (only once)
    if (currentView === 'detail' && selectedItem && sidebarRef.current && !hasScrolledToSelected) {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      scrollTimeout.current = setTimeout(() => {
        const selectedElement = sidebarRef.current.querySelector(`[data-item-id="${selectedItem.id}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHasScrolledToSelected(true);
        }
      }, 100);
    }
  }, [currentView, selectedItem, hasScrolledToSelected]);

  // FIXED: Update filteredSidebarData when search term changes in detail view
  useEffect(() => {
    if (currentView === 'detail') {
      setFilteredSidebarData(getSortedData(filteredItems));
    }
  }, [filteredItems, currentView, sortConfig]);

  const deleteItem = useCallback((id) => {
    Swal.fire({
      title: "Are you sure you want to delete this Item?",
      text: ".",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#4d4d4d",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        InventoryService.deleteItem(id)
          .then((response) => {
            setMessage("Item deleted Successfully");
            setLoading(true);
            Swal.fire({
              title: "Item deleted Successfully",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#4d4d4d",
            }).then(() => {
              InventoryService.retriveAllItems(currentTenant).then((response) => {
                const sortedItems = response.data.sort((a, b) => (a.id < b.id ? 1 : -1));
                setItems(sortedItems);
                setFilteredItems(sortedItems);
                setLoading(false);
                if (selectedItem && selectedItem.id === id) {
                  setCurrentView('list');
                  setSelectedItem(null);
                }
              });
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#4d4d4d",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#4d4d4d",
              });
            }
          });
      }
    });
  }, [navigate, selectedItem]);

  const pdf = useCallback(() => {
    const curdate = moment().format("DD-MM-YYYY HH:mm:ss");
    const columns = [
      { header: "Item Name", dataKey: "name" },
      { header: "Item Code", dataKey: "itemcode" },
      { header: "HSN Code", dataKey: "hsn" },
      { header: "Tax(%)", dataKey: "tax" },
      { header: "Total Stock", dataKey: "stock" },
      { header: "Unit of Measurement", dataKey: "uomname" },
      { header: "Category", dataKey: "category" },
      { header: "Latest Purchase Price", dataKey: "cost" },
    ];

    var doc = new jsPDF("p", "pt", [842, 595]);
    autoTable(doc, {
      theme: "grid",
      startY: 120,
      columnStyles: { 7: { halign: "right" } },
      didDrawPage: (HookData) => {
        HookData.settings.margin.top = 120;
      },
      body: items,
      columns: columns,
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageSize = doc.internal.pageSize;
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
      const footer = `Page ${i} of ${pageCount}`;

      doc.addImage(logo, "GIF", 40, 20, 60, 60, { baseline: "top" });
      doc.setFontSize(8);
      doc.text(`${curdate}`, 560, 20, "right");
      doc.setFontSize(16);
      doc.text(`${clientname}`, 180, 40, { baseline: "top" });
      doc.setFontSize(14);
      doc.text("Items/Products", 210, 70, { baseline: "top" });
      doc.line(0, 90, 600, 90);
      doc.setFontSize(10);

      doc.text(
        footer,
        pageWidth / 2 - doc.getTextWidth(footer) / 2,
        pageHeight - 15,
        { baseline: "bottom" }
      );
    }
    doc.save("ItemTable.pdf");
  }, [items]);

  // FIXED: Enhanced search to include numeric fields (price and stock)
  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredItems(items);
    } else {
      const filtered = items.filter(item => {
        const searchLower = term.toLowerCase();
        const stockString = item.stock?.toString() || '';
        const costString = formatCurrencyAsString(item.cost);

        return (
          item.name?.toLowerCase().includes(searchLower) ||
          item.itemcode?.toLowerCase().includes(searchLower) ||
          item.category?.toLowerCase().includes(searchLower) ||
          item.brand?.toLowerCase().includes(searchLower) ||
          item.hsn?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower) ||
          stockString.includes(term) || // Search by stock number
          costString.includes(term) || // Search by price/number
          item.tax?.toString().includes(term) // Search by tax percentage
        );
      });
      setFilteredItems(filtered);
    }
    setCurrentPage(1);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setCurrentView('detail');
    setHasScrolledToSelected(false);
    // FIXED: Set filteredSidebarData to current filteredItems when entering detail view
    setFilteredSidebarData(getSortedData(filteredItems));
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedItem(null);
    setHasScrolledToSelected(false);
  };

  const getStockStatus = (stock) => {
    if (stock === null || stock === undefined) return { text: '0', style: 'UI-stock-low' };
    if (stock > 5) return { text: stock, style: 'UI-stock-high' };
    if (stock > 3) return { text: stock, style: 'UI-stock-medium' };
    return { text: stock, style: 'UI-stock-low' };
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0';
    return (
      <NumericFormat
        displayType="text"
        value={value}
        decimalScale={2}
        fixedDecimalScale={true}
        thousandSeparator={true}
        thousandsGroupStyle={thousandsdisplay}
        prefix={currency === "INR" ? "₹" : currency === "USD" ? "$" : currency}
      />
    );
  };

  // Pagination functions
  const getCurrentPageItems = () => {
    const sortedData = getSortedData(filteredItems);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (filteredItems.length <= itemsPerPage) return null;

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} items
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <TextField
            select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            variant="outlined"
            size="small"
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <MenuItem value={5}>5 per page</MenuItem>
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={20}>20 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
          </TextField>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && !(currentPage === 1) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && !(currentPage === 1) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && !(currentPage === totalPages) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && !(currentPage === totalPages) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageItems();

    if (filteredItems.length === 0) {
      return (
        <div className={`UI-no-items-message ${isMobile ? 'UI-no-items-message-mobile' : ''}`}>
          {searchTerm ? 'No items found matching your search' : 'No items available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'name' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('name')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Item Name
                    <SortIndicator columnKey="name" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'brand' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('brand')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Brand
                    <SortIndicator columnKey="brand" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'stock' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('stock')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Stock
                    <SortIndicator columnKey="stock" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'cost' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('cost')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Price
                    <SortIndicator columnKey="cost" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'description' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('description')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Description
                    <SortIndicator columnKey="description" />
                  </div>
                </th>
                {/* FIXED: Always render Actions column but hide it conditionally */}
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${(inventoryid === "2" || inventoryid === "3") ? '' : 'UI-hidden'}`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map(item => {
                const stockStatus = getStockStatus(item.stock);
                const isStockHovered = hovered === `stock-${item.id}`;
                const isPriceHovered = hovered === `price-${item.id}`;

                return (
                  <tr
                    key={item.id}
                    className={`UI-table-row ${isMobile ? 'UI-table-row-mobile' : ''} ${hovered === item.id ? (isMobile ? '' : 'UI-table-row-hovered') : ''}`}
                    onClick={() => handleItemClick(item)}
                    onMouseEnter={() => setHovered(item.id)}
                    onMouseLeave={() => {
                      setHovered(null);
                      setIsActionHovered(null);
                    }}
                  >
                    {/* Item Name Cell */}
                    <td
                      className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} UI-item-name ${hovered === item.id ? 'UI-item-name-hover' : ''}`}
                    >
                      <Tooltip
                        title="Details"
                        placement="right"
                        arrow
                        slotProps={{
                          popper: {
                            sx: { pointerEvents: 'none' },
                          },
                        }}
                        open={!isMobile && hovered === item.id && !isActionHovered}
                      >
                        <span>{highlightSearchText(item.name, searchTerm)}</span>
                      </Tooltip>
                    </td>

                    {/* Brand Cell */}
                    <td
                      className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} ${hovered === item.id ? 'UI-brand-hover' : ''}`}
                    >
                      {highlightSearchText(item.brand, searchTerm)}
                    </td>

                    {/* Stock Column */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {item.stock > 0 ? (
                        <span className={stockStatus.style}>
                          {highlightSearchText(stockStatus.text.toString(), searchTerm)}
                        </span>
                      ) : item.cost > 0 ? (
                        <Tooltip
                          title="Add"
                          placement="left"
                          open={!isMobile && hovered === `stock-${item.id}`}
                          arrow
                          slotProps={{
                            popper: {
                              sx: { pointerEvents: 'none' },
                            },
                          }}
                        >
                          <Link
                            to="/addstock"
                            state={{ id: -1, pagename: "Add Stock", data: undefined, stocktype: "Own", items: item }}
                            className={`UI-add-action-button ${isMobile ? 'UI-add-action-button-mobile' : ''} ${hovered === `stock-${item.id}` ? 'UI-add-action-button-hover' : ''}`}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={() => setHovered(`stock-${item.id}`)}
                            onMouseLeave={() => setHovered(null)}
                          >
                            <img
                              src={AddIcon}
                              alt="Add Stock"
                              className={`UI-add-icon ${isMobile ? 'UI-add-icon-mobile' : ''} ${hovered === `stock-${item.id}` ? 'UI-add-icon-hover' : ''}`}
                            />
                            Stock
                          </Link>
                        </Tooltip>
                      ) : (
                        <span className="UI-stock-low">
                          {highlightSearchText('0', searchTerm)}
                        </span>
                      )}
                    </td>

                    {/* Price Column */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {item.cost > 0 ? (
                        highlightSearchText(formatCurrencyAsString(item.cost), searchTerm)
                      ) : (
                        <Tooltip
                          title="Add"
                          placement="left"
                          open={!isMobile && hovered === `price-${item.id}`}
                          arrow
                          slotProps={{
                            popper: {
                              sx: { pointerEvents: 'none' },
                            },
                          }}
                        >
                          <Link
                            to="/addcost"
                            state={{ pagename: "Add Price", item: item }}
                            className={`UI-add-action-button ${isMobile ? 'UI-add-action-button-mobile' : ''} ${hovered === `price-${item.id}` ? 'UI-add-action-button-hover' : ''}`}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={() => setHovered(`price-${item.id}`)}
                            onMouseLeave={() => setHovered(null)}
                          >
                            <img
                              src={AddIcon}
                              alt="Add Price"
                              className={`UI-add-icon ${isMobile ? 'UI-add-icon-mobile' : ''} ${hovered === `price-${item.id}` ? 'UI-add-icon-hover' : ''}`}
                            />
                            Price
                          </Link>
                        </Tooltip>
                      )}
                    </td>

                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} UI-description ${isMobile ? 'UI-description-mobile' : ''}`} title={item.description}>
                      {highlightSearchText(item.description || 'No description', searchTerm)}
                    </td>

                    {/* Actions Column - Always rendered but hidden conditionally */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} ${(inventoryid === "2" || inventoryid === "3") ? '' : 'UI-hidden'}`}>
                      <div
                        className="UI-action-buttons flex justify-start items-center gap-"
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {/* Edit Item */}
                        <Link
                          to={{ pathname: "/newitems" }}
                          state={{
                            id: item.id,
                            iseditable: item.iseditable,
                            pagename: "Update Item",
                          }}
                          className="UI-edit-link"
                          onClick={(e) => e.stopPropagation()}
                          onMouseEnter={() => setIsActionHovered(`editItem-${item.id}`)}
                          onMouseLeave={() => setIsActionHovered(null)}
                        >
                          <Tooltip
                            title="Edit"
                            arrow
                            slotProps={{
                              popper: {
                                sx: { pointerEvents: 'none' },
                              },
                            }}
                            placement="left"
                            open={!isMobile && isActionHovered === `editItem-${item.id}`}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className={`UI-edit-icon ${isMobile ? 'UI-edit-icon-mobile' : ''} ${isActionHovered === `editItem-${item.id}` ? 'UI-edit-icon-hover' : ''
                                }`}
                            />
                          </Tooltip>
                        </Link>

                        {/* Delete Item */}
                        {(!item.cost || item.cost === null) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteItem(item.id);
                            }}
                            className="UI-delete-button UI-delete-button-list"
                            aria-label="Delete item"
                            onMouseEnter={() => setIsActionHovered(`deleteItem-${item.id}`)}
                            onMouseLeave={() => setIsActionHovered(null)}
                          >
                            <Tooltip
                              title="Delete"
                              arrow
                              slotProps={{
                                popper: {
                                  sx: { pointerEvents: 'none' },
                                },
                              }}
                              placement="right"
                              open={!isMobile && isActionHovered === `deleteItem-${item.id}`}
                            >
                              <img
                                src={DeleteIcon}
                                alt="Delete"
                                className={`UI-delete-icon ${isMobile ? 'UI-delete-icon-mobile' : ''} ${isActionHovered === `deleteItem-${item.id}` ? 'UI-delete-icon-hover' : ''
                                  }`}
                              />
                            </Tooltip>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  const renderDetailView = () => {
    if (!selectedItem) return null;

    // FIXED: Define stockStatus here for the detail view
    const stockStatus = getStockStatus(selectedItem.stock);

    return (
      <div className="UI-detail-view">
        {/* Sidebar with item list */}
        <div className="UI-sidebar">
          <div className="UI-sidebar-header">
            <button
              className={`UI-back-button ${isBackHovered ? 'UI-back-button-hover' : ''}`}
              onClick={handleBackToList}
              onMouseEnter={() => setIsBackHovered(true)}
              onMouseLeave={() => setIsBackHovered(false)}
            >
              <i className="fa fa-arrow-left" /> Back
            </button>
          </div>

          <div className="UI-sidebar-list" ref={sidebarRef}>
            {/* FIXED: Use filteredItems instead of filteredSidebarData for proper search filtering */}
            {getSortedData(filteredItems).map((item) => (
              <div
                key={item.id}
                data-item-id={item.id}
                className={`UI-sidebar-item ${selectedItem.id === item.id ? 'UI-sidebar-item-active' : ''} ${hovered === item.id ? 'UI-sidebar-item-hover' : ''}`}
                onClick={() => setSelectedItem(item)}
                onMouseEnter={() => setHovered(item.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className={`UI-sidebar-item-name ${hovered === item.id ? 'UI-sidebar-item-name-hover' : ''}`}>
                  {highlightSearchText(item.name, searchTerm)}
                </div>
                <div className={`UI-sidebar-item-code ${hovered === item.id ? 'UI-sidebar-item-code-hover' : ''}`}>
                  {highlightSearchText(item.itemcode, searchTerm)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Content */}
        <div className="UI-detail-content">
          <div className="UI-detail-header">
            <div className="UI-detail-title-wrapper">
              <h2 className="UI-detail-title">{highlightSearchText(selectedItem.name, searchTerm)}</h2>
              <p className="UI-detail-subtitle">
                Item ID: {highlightSearchText(selectedItem.id, searchTerm)}
              </p>
            </div>

            <div className="UI-detail-actions">
              {/* Add Stock */}
              {selectedItem.cost > 0 && Number(selectedItem.stock) <= 0 && (
                <Link
                  to="/addstock"
                  state={{ id: -1, pagename: "Add Stock", stocktype: "Own" }}
                  className={`UI-add-action-button ${isMobile ? 'UI-add-action-button-mobile' : ''} ${hovered === 'detail-stock' ? 'UI-add-action-button-hover' : ''}`}
                  onMouseEnter={() => setHovered('detail-stock')}
                  onMouseLeave={() => setHovered(null)}
                >
                  <img
                    src={AddIcon}
                    alt="Add Stock"
                    className={`UI-add-icon ${isMobile ? 'UI-add-icon-mobile' : ''} ${hovered === 'detail-stock' ? 'UI-add-icon-hover' : ''}`}
                  />
                  {isMobile ? 'Stock' : 'Add Stock'}
                </Link>
              )}

              {/* Add Price */}
              {(!selectedItem.cost || selectedItem.cost === 0) && (
                <Link
                  to="/addcost"
                  state={{ id: selectedItem.id, pagename: "Add Price", item: selectedItem }}
                  className={`UI-add-action-button ${isMobile ? 'UI-add-action-button-mobile' : ''} ${hovered === 'detail-price' ? 'UI-add-action-button-hover' : ''}`}
                  onMouseEnter={() => setHovered('detail-price')}
                  onMouseLeave={() => setHovered(null)}
                >
                  <img
                    src={AddIcon}
                    alt="Add Price"
                    className={`UI-add-icon ${isMobile ? 'UI-add-icon-mobile' : ''} ${hovered === 'detail-price' ? 'UI-add-icon-hover' : ''}`}
                  />
                  {isMobile ? 'Price' : 'Add Price'}
                </Link>
              )}

              {/* Edit Item */}
              <Link
                to={{ pathname: "/newitems" }}
                state={{ id: selectedItem.id, iseditable: selectedItem.iseditable, pagename: "Update Item" }}
                className={`UI-action-button ${isMobile ? 'UI-action-button-mobile' : ''} UI-edit-button ${hovered === "editItem" ? 'UI-edit-button-hover' : ''}`}
                onMouseEnter={() => setHovered("editItem")}
                onMouseLeave={() => setHovered(null)}
              >
                <Tooltip title="Edit Item" arrow slotProps={{
                  popper: {
                    sx: { pointerEvents: 'none' },
                  },
                }}>
                  <img
                    src={EditIcon}
                    alt="Edit"
                    className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hovered === "editItem" ? 'UI-action-icon-hover' : ''}`}
                  />
                </Tooltip>
              </Link>

              {/* Delete Item */}
              {(!selectedItem.cost || selectedItem.cost === null) && (
                <button
                  onClick={() => deleteItem(selectedItem.id)}
                  className={`UI-action-button ${isMobile ? 'UI-action-button-mobile' : ''} UI-delete-button ${hovered === "deleteItem" ? 'UI-delete-button-hover' : ''}`}
                  onMouseEnter={() => setHovered("deleteItem")}
                  onMouseLeave={() => setHovered(null)}
                >
                  <Tooltip title="Delete Item" arrow slotProps={{
                    popper: {
                      sx: { pointerEvents: 'none' },
                    },
                  }}>
                    <img
                      src={DeleteIcon}
                      alt="Delete"
                      className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hovered === "deleteItem" ? 'UI-action-icon-hover' : ''}`}
                    />
                  </Tooltip>
                </button>
              )}
            </div>
          </div>

          <div className="UI-detail-grid">
            {/* Basic Info */}
            <div className="UI-detail-section">
              <h3 className="UI-section-title">Basic Information</h3>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Item Code</span>
                <span className={`UI-item-code ${isMobile ? 'UI-item-code-mobile' : ''}`}>{highlightSearchText(selectedItem.itemcode, searchTerm)}</span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">HSN Code</span>
                <span className="UI-detail-value">{highlightSearchText(selectedItem.hsn, searchTerm)}</span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Unit</span>
                <span className="UI-detail-value">{highlightSearchText(selectedItem.uomname, searchTerm)}</span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Brand</span>
                <span className="UI-detail-value">{highlightSearchText(selectedItem.brand, searchTerm)}</span>
              </div>
              {selectedItem.model && (
                <div className="UI-detail-row">
                  <span className="UI-detail-label">Model</span>
                  <span className="UI-detail-value">{highlightSearchText(selectedItem.model, searchTerm)}</span>
                </div>
              )}
            </div>

            {/* Category Information */}
            <div className="UI-detail-section">
              <h3 className="UI-section-title">Category Information</h3>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Main Category</span>
                <span className="UI-detail-value">{highlightSearchText(selectedItem.mcategory, searchTerm)}</span>
              </div>
              {selectedItem.categorylvl2 && (
                <div className="UI-detail-row">
                  <span className="UI-detail-label">Sub Category</span>
                  <span className="UI-detail-value">{highlightSearchText(selectedItem.categorylvl2, searchTerm)}</span>
                </div>
              )}
              <div className="UI-detail-row">
                <span className="UI-detail-label">Category Level 3</span>
                <span className={`UI-category-badge ${isMobile ? 'UI-category-badge-mobile' : ''}`}>{highlightSearchText(selectedItem.category, searchTerm)}</span>
              </div>
            </div>

            {/* Stock & Price */}
            <div className="UI-detail-section">
              <h3 className="UI-section-title">Stock & Pricing</h3>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Stock</span>
                <span className={`UI-detail-value ${stockStatus.style}`}>
                  {highlightSearchText(stockStatus.text.toString(), searchTerm)} {selectedItem.uomname}
                </span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Purchase Price</span>
                <span className="UI-detail-value">
                  {highlightSearchText(formatCurrencyAsString(selectedItem.cost), searchTerm)}
                </span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Tax Rate</span>
                <span className={`UI-tax-badge ${isMobile ? 'UI-tax-badge-mobile' : ''}`}>{highlightSearchText(selectedItem.tax, searchTerm)}%</span>
              </div>
            </div>

            {/* Description */}
            {selectedItem.description && (
              <div className="UI-detail-section" style={{ gridColumn: '1 / -1' }}>
                <h3 className="UI-section-title">Description</h3>
                <p className="UI-description-text">
                  {highlightSearchText(selectedItem.description, searchTerm)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
      <div className="UI-card">
        {/* Header */}
        <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
        <Breadcrumb breadcrumbs={[{name:"Home",path:"/inventory"},{name:"Items/Products",path:null}]}/>

          <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
            <div>
              <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
              Items/Products
              </h1>
              <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                {currentView === 'detail' && selectedItem
                  ? `Item ID: ${selectedItem.id}`
                  : 'Manage your Product catalog'
                }
              </p>
            </div>

            <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
              {/* Mobile Search - Always visible on mobile */}
              {isMobile && (
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="UI-mobile-search-input"
                />
              )}

              <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                {/* FIXED: Desktop Search Toggle - Now exactly matches QuotationTable */}
                {!isMobile && (
                  <div
                    className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                    onClick={() => {
                      if (!showSearch) setShowSearch(true);
                    }}
                    onMouseEnter={() => setHovered("search")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i className="fa fa-search UI-search-icon" />
                    {!showSearch && "Search"}
                    {showSearch && (
                      <input
                        type="text"
                        placeholder="Search items..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="UI-search-input"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                          if (searchTerm.trim() === "") {
                            setShowSearch(false);
                          }
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Export button */}
                <button
                  className={`UI-btn ${isMobile ? 'UI-btn-mobile' : ''} UI-btn-outline ${hovered === "export" ? 'UI-btn-outline-hover' : ''}`}
                  onClick={pdf}
                  onMouseEnter={() => setHovered("export")}
                  onMouseLeave={() => setHovered(null)}
                >
                  <i className="fa fa-file-pdf" /> {isMobile ? "Export" : "Export"}
                </button>

                {/* New Item button */}
                {(inventoryid === "2" || inventoryid === "3") && (
                  <Link
                    to={{ pathname: "/newitems" }}
                    state={{ id: -1, pagename: "New Item" }}
                    className={`UI-btn ${isMobile ? 'UI-btn-mobile' : ''} UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''}`}
                    onMouseEnter={() => setHovered("new")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i className="fa fa-plus" /> {isMobile ? "New" : "New Item"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="UI-loading-container">
            {spin}
          </div>
        ) : (
          <>
            {/* Switch between list and detail view */}
            {currentView === 'list'
              ? renderTable()
              : renderDetailView()
            }
          </>
        )}
      </div>
    </div>
  );
};

export default withRouter(ItemsDataTable);